import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuote } from '../contexts/QuoteContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, FileText, Mail, Download, Plus } from 'lucide-react';

const QuoteSuccess = () => {
    const navigate = useNavigate();
    const { createdQuote, resetQuote } = useQuote();
    const isNavigating = useRef(false);

    // Redirect if no quote data (but not if we're navigating away)
    React.useEffect(() => {
        if (!createdQuote && !isNavigating.current) {
            navigate('/');
        }
    }, [createdQuote, navigate]);

    const handleNewQuote = () => {
        isNavigating.current = true;
        resetQuote();
        navigate('/');
    };

    const handleViewQuotes = () => {
        isNavigating.current = true;
        resetQuote();
        navigate('/quotes');
    };

    if (!createdQuote) {
        return null;
    }

    return (
        <div className="max-w-lg mx-auto animate-fade-in">
            <Card className="shadow-lg border-0 overflow-hidden">
                {/* Success Header */}
                <div className="bg-[#7AC943] p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
                        <CheckCircle className="h-10 w-10 text-black" />
                    </div>
                    <h1 className="text-2xl font-bold font-heading text-black mb-1">
                        Quote Sent Successfully!
                    </h1>
                    <p className="text-black/70">
                        The quote has been emailed to the customer
                    </p>
                </div>

                <CardContent className="p-6 space-y-6">
                    {/* Quote Reference */}
                    <div className="text-center py-4 bg-brand-gray-50 rounded-lg">
                        <span className="text-xs font-bold uppercase tracking-wide text-brand-gray-500">
                            Quote Reference
                        </span>
                        <p className="text-2xl font-bold font-heading text-brand-charcoal mt-1" data-testid="quote-reference">
                            {createdQuote.reference_number}
                        </p>
                    </div>

                    {/* Summary */}
                    <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-brand-gray-100">
                            <span className="text-brand-gray-500">Customer</span>
                            <span className="font-medium text-brand-charcoal">
                                {createdQuote.customer.name}
                            </span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-brand-gray-100">
                            <span className="text-brand-gray-500">Pack</span>
                            <span className="font-medium text-brand-charcoal">
                                {createdQuote.spare_pack.name}
                            </span>
                        </div>
                        <div className="flex justify-between py-2">
                            <span className="text-brand-gray-500">Total</span>
                            <span className="text-xl font-bold font-heading text-brand-charcoal">
                                £{createdQuote.total_price.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                            </span>
                        </div>
                    </div>

                    {/* What was sent */}
                    <div className="p-4 bg-blue-50 rounded-lg space-y-2">
                        <h4 className="text-sm font-bold text-blue-800">Emails Sent</h4>
                        <div className="flex items-center gap-2 text-sm text-blue-700">
                            <Mail className="h-4 w-4" />
                            <span>Quote PDF to {createdQuote.customer.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-blue-700">
                            <FileText className="h-4 w-4" />
                            <span>Internal copy to sales team</span>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3 pt-2">
                        <Button
                            onClick={handleNewQuote}
                            className="w-full h-12 bg-[#7AC943] hover:bg-[#68B035] text-black font-bold uppercase tracking-wide"
                            data-testid="create-new-quote-btn"
                        >
                            <Plus className="h-5 w-5 mr-2" />
                            Create New Quote
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleViewQuotes}
                            className="w-full h-12"
                            data-testid="view-quotes-btn"
                        >
                            <FileText className="h-5 w-5 mr-2" />
                            View My Quotes
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default QuoteSuccess;
